import React, { memo, useMemo } from 'react'
import { Tabs } from 'antd'
import { Icon } from '@iconify/react'

import CopyWidgetLinkButton from './CopyWidgetLinkButton'
import ProjectTaskToolbar from './ProjectTaskToolbar'
import ProjectTabsScrollArrows from './ProjectTabsScrollArrows'
import {
    FILES_TAB_BAR_HOST_ATTR,
    FILES_TAB_BAR_SLOT_ATTR,
    PROTOTYPE_TAB_BAR_HOST_ATTR,
    PROTOTYPE_TAB_BAR_SLOT_ATTR,
    STORY_MAP_TAB_BAR_HOST_ATTR,
    STORY_MAP_TAB_BAR_SLOT_ATTR,
} from './projectTabBarPortal'
import styles from '../projectDetail.module.css'

const TabLabel = ({ icon, children, iconStyle }) => (
    <span className={styles.topTabLabel}>
        <Icon icon={icon} width={16} height={16} style={iconStyle} />
        {children}
    </span>
)

const PortalSlot = ({ hostAttribute, slotAttribute }) => (
    <div className={styles.tabBarSupportRow}>
        <div {...{ [hostAttribute]: 'true' }}>
            <div {...{ [slotAttribute]: 'true' }} />
        </div>
    </div>
)

const ProjectMainTabs = ({
    activeMainTab,
    onTabChange,
    projectId,
    canViewStoryMap,
    canViewPrototype,
    canViewSupportTab,
    canAccessDevelopmentTab,
    canViewTestRun,
    canCopyWidgetLink,
    canAccessStoryMapRoute,
    canAccessPrototypeRoute,
    developmentSubView,
    supportSubView,
    addTaskToFirstSwimlaneRef,
    setTaskListSearchTerm,
    applyDevelopmentToolbarQuery,
    applySupportToolbarQuery,
}) => {
    const tabItems = useMemo(
        () => [
            ...(canViewStoryMap
                ? [
                      {
                          key: 'storyMap',
                          label: (
                              <TabLabel icon="mdi:map-marker-path">
                                  Story Map
                              </TabLabel>
                          ),
                      },
                  ]
                : []),
            ...(canViewPrototype
                ? [
                      {
                          key: 'prototype',
                          label: (
                              <TabLabel icon="mdi:vector-polyline">
                                  Prototype
                              </TabLabel>
                          ),
                      },
                  ]
                : []),
            ...(canViewSupportTab
                ? [
                      {
                          key: 'support',
                          label: (
                              <TabLabel
                                  icon="si:bar-chart-line"
                                  iconStyle={{
                                      transform: 'rotate(-180deg)',
                                  }}
                              >
                                  Support
                              </TabLabel>
                          ),
                      },
                  ]
                : []),
            ...(canAccessDevelopmentTab
                ? [
                      {
                          key: 'development',
                          label: (
                              <TabLabel icon="mdi:code-tags">
                                  Development
                              </TabLabel>
                          ),
                      },
                  ]
                : []),
            {
                key: 'files',
                label: <TabLabel icon="codicon:files">Files</TabLabel>,
            },
            ...(canViewTestRun
                ? [
                      {
                          key: 'testrun',
                          label: (
                              <TabLabel icon="streamline:test-tube">
                                  Test Run
                              </TabLabel>
                          ),
                      },
                  ]
                : []),
        ],
        [
            canViewStoryMap,
            canViewPrototype,
            canViewSupportTab,
            canAccessDevelopmentTab,
            canViewTestRun,
        ]
    )

    let toolbar = null
    if (activeMainTab === 'development') {
        toolbar = (
            <div className={styles.tabBarSupportRow}>
                <ProjectTaskToolbar
                    taskListKind="main"
                    onAddTask={(type = 'Task') =>
                        addTaskToFirstSwimlaneRef.current?.(type)
                    }
                    onSearch={(value) => {
                        setTaskListSearchTerm(value)
                        applyDevelopmentToolbarQuery({ search: value })
                    }}
                    projectId={projectId}
                    onFilterChange={(filter) => {
                        if (
                            filter &&
                            Object.prototype.hasOwnProperty.call(
                                filter,
                                'search'
                            )
                        ) {
                            setTaskListSearchTerm(filter.search || '')
                        }
                        applyDevelopmentToolbarQuery(filter)
                    }}
                    activeViewTab={developmentSubView}
                />
            </div>
        )
    } else if (activeMainTab === 'support' && canViewSupportTab) {
        toolbar = (
            <div className={styles.tabBarSupportRow}>
                {canCopyWidgetLink && projectId ? (
                    <CopyWidgetLinkButton
                        projectId={projectId}
                        variant="toolbar"
                    />
                ) : null}
                <ProjectTaskToolbar
                    taskListKind="support"
                    onAddTask={() =>
                        addTaskToFirstSwimlaneRef.current?.()
                    }
                    onSearch={(value) => {
                        setTaskListSearchTerm(value)
                        applySupportToolbarQuery({ search: value })
                    }}
                    projectId={projectId}
                    onFilterChange={(filter) => {
                        if (
                            filter &&
                            Object.prototype.hasOwnProperty.call(
                                filter,
                                'search'
                            )
                        ) {
                            setTaskListSearchTerm(filter.search || '')
                        }
                        applySupportToolbarQuery(filter)
                    }}
                    activeViewTab={supportSubView}
                />
            </div>
        )
    } else if (activeMainTab === 'storyMap' && canAccessStoryMapRoute) {
        toolbar = (
            <PortalSlot
                hostAttribute={STORY_MAP_TAB_BAR_HOST_ATTR}
                slotAttribute={STORY_MAP_TAB_BAR_SLOT_ATTR}
            />
        )
    } else if (activeMainTab === 'prototype' && canAccessPrototypeRoute) {
        toolbar = (
            <PortalSlot
                hostAttribute={PROTOTYPE_TAB_BAR_HOST_ATTR}
                slotAttribute={PROTOTYPE_TAB_BAR_SLOT_ATTR}
            />
        )
    } else if (activeMainTab === 'files') {
        toolbar = (
            <PortalSlot
                hostAttribute={FILES_TAB_BAR_HOST_ATTR}
                slotAttribute={FILES_TAB_BAR_SLOT_ATTR}
            />
        )
    }

    return (
        <div data-project-main-tabs>
            <Tabs
                activeKey={activeMainTab}
                onChange={onTabChange}
                tabBarExtraContent={
                    <div className={styles.tabBarExtraWithScroll}>
                        <ProjectTabsScrollArrows rootSelector="[data-project-main-tabs]" />
                        {toolbar}
                    </div>
                }
                items={tabItems}
                style={{ marginTop: '12px' }}
                className={styles.viewTabs}
            />
        </div>
    )
}

export default memo(ProjectMainTabs)
